/**
 * The Actions tab's list.
 *
 * The master's Actions filters itself with To do / Done / All pills and groups
 * by urgency, which is a lot of chrome for a list rarely longer than a phone
 * screen: the filter has to be read before the rows are. Here it is the rows.
 */
export const ARCHITECTURE_ACTIONS: {
  id: string;
  label: string;
  note: string;
}[] = [
  {
    id: 'calculator',
    label: 'Complete benefits calculator',
    note: 'Eleven questions left. It works out which claim to start with.',
  },
  {
    id: 'ni',
    label: "Add Margaret's National Insurance number",
    note: 'The Attendance Allowance claim needs it before it can be sent.',
  },
  {
    id: 'address',
    label: "Check Margaret's address",
    note: 'We read this off a letter in March and nobody has confirmed it.',
  },
];

/**
 * What the Help tab holds.
 *
 * The articles were on home, under the actions, where they competed with the
 * thing somebody came to do. Help is where you go when you are stuck, and being
 * stuck is answered either by reading something or by asking a person — so both
 * live here, with the person first.
 */
export const ARCHITECTURE_HELP = {
  talk: 'Talk to us',
  articlesTitle: 'Help articles',
  articles: [
    {
      id: 'funding',
      title: 'Understanding care funding options',
      summary: 'A plain-English guide to who pays for care and how.',
    },
    {
      id: 'attendance-allowance',
      title: 'What Attendance Allowance is worth',
      summary: 'The two rates, who qualifies, and what it does not affect.',
    },
    {
      id: 'attorney',
      title: 'Setting up power of attorney',
      summary: 'What it covers, what it costs, and when to do it.',
    },
  ],
};

/**
 * What each home card explains before it starts.
 *
 * Lifted from production, not written here: the three form flows all open
 * `FormIntroPage` (a title, a blurb, three icon lines and a "Did you know?"),
 * and Power of attorney opens a menu of four ways in instead. Copy is theirs
 * verbatim so the sheet is a change of container rather than a rewrite.
 *
 * In the app these are full screens with a back chevron, which makes reading
 * about a thing feel like starting it. As sheets they are a look rather than a
 * commitment: tap outside and you are back on home.
 */
export type CardIntro = {
  title: string;
  description: string;
  /** The three lines production shows, each with its icon. */
  items?: { icon: string; text: string }[];
  /** Production's "Did you know?" footnote. */
  infoText?: string;
  /**
   * Questions people arrive with, answered in place.
   *
   * Power of attorney is the one card where somebody taps without having
   * decided anything: they want to know what an LPA is before they want to make
   * one. These were four tappable rows opening sheets, which made reading two
   * of them a matter of opening and closing two layers. An accordion answers
   * them where they are asked.
   */
  faqs?: { id: string; question: string; answer: string[] }[];
  /** The button. Production says Continue on all three form intros. */
  action?: string;
};

export const CARD_INTROS: Record<string, CardIntro> = {
  'Financial status': {
    title: 'Financial status',
    description:
      'Understanding your financial situation helps us identify funding options and benefits you may be entitled to, so you can make informed decisions about care.',
    items: [
      {
        icon: 'file-contract',
        text: 'Answer financial questions about the person requiring care.',
      },
      {
        icon: 'circle-check',
        text: 'Our experts will review and calculate your bespoke funding options.',
      },
      {
        icon: 'house',
        text: "We'll send you a My Funding Guide and help you apply for funding/benefits, if relevant.",
      },
    ],
    infoText:
      "We've helped thousands of families claim funding they didn't realise they were eligible for.",
    action: 'Continue',
  },
  'Care assessment': {
    title: 'Care assessment',
    description:
      'It takes about 30 minutes. There are a lot of questions, and the more you answer the more useful it is. Answer what you can and come back to the rest.',
    items: [
      {
        icon: 'circle-check',
        text: 'Answer questions about the care needs of your loved one.',
      },
      {
        icon: 'user',
        text: 'Talk it through with your care expert.',
      },
      {
        icon: 'file-contract',
        text: 'Get a care search shortlist tailored to the answers.',
      },
    ],
    action: 'Continue',
  },
  'Benefits calculator': {
    title: 'Benefits calculator',
    description:
      'Answer a few questions about your circumstances to find out which government benefits and funding you may be eligible for.',
    items: [
      {
        icon: 'circle-check',
        text: 'Answer questions about your circumstances to check eligibility for benefits.',
      },
      {
        icon: 'circle-check',
        text: 'Get instant results showing which benefits you may be eligible for.',
      },
      {
        icon: 'file-contract',
        text: 'Receive guidance on how to apply for the benefits you qualify for.',
      },
    ],
    infoText:
      "We've helped thousands of families discover benefits they didn't realise they were eligible for.",
    action: 'Continue',
  },
  'Power of attorney': {
    title: 'Power of attorney',
    description:
      'Whether you\u2019re learning or ready to apply, we\u2019re here to help.',
    faqs: [
      {
        id: 'what',
        question: 'What is power of attorney?',
        answer: [
          'A Lasting Power of Attorney (LPA) is a legal document that lets someone you trust make decisions on your behalf if you\u2019re unable to.',
          'Property and financial affairs covers things like paying bills, managing a bank account, or selling a home. This can be used as soon as it\u2019s registered, even while the person still has capacity.',
          'Health and welfare covers decisions about medical treatment, daily care, and where someone lives. This can only be used after the person has lost the capacity to decide for themselves.',
          'You can set up one or both. Most people set up both.',
        ],
      },
      {
        id: 'why',
        question: 'Why set up power of attorney?',
        answer: [
          'If someone loses the ability to make decisions and there\u2019s no LPA in place, no one, not even their spouse or children, can legally act on their behalf.',
          'The alternative is applying to the Court of Protection, which can take over a year, costs significantly more, and adds stress at an already difficult time.',
        ],
      },
      {
        id: 'guidance',
        question: 'Can I talk it through first?',
        answer: [
          'Yes. Book a call with the care team and they will talk through whether an LPA is the right next step and what it involves.',
        ],
      },
    ],
    action: 'Start applying',
  },
};

/**
 * The care hub, where Care and Profile become one tab: a section per part of
 * someone's care, in the order a family reaches for them. The labels are the
 * master's own (Profile's pills and the Care tab), so nothing gets renamed by
 * moving.
 */
export const CARE_HUB = {
  sections: [
    { id: 'care', label: 'Arranging care' },
    { id: 'assess', label: 'Assess' },
    { id: 'about', label: 'About' },
    { id: 'docs', label: 'Docs' },
    { id: 'calls', label: 'Calls' },
  ] as const,
  /** Under every section for someone just added, before there is anything. */
  empty: 'Nothing yet',
};

/**
 * Home in the architecture concept with Care as one: the lead, what put it
 * first, the conversations still going, and a way to start a new one. A
 * conversation per thing, never one long thread: a task like Attendance
 * Allowance starts its own. Reuses wording from the Home concepts; the
 * "Carry on" label is new.
 */
export const ARCH_HOME = {
  lead: {
    label: 'Things we can help with',
    body: (person: string) =>
      `From what you've told us, we could help you work out what you and ${person} are entitled to.`,
    action: 'Check eligibility',
  },
  toldUs: {
    label: `What you've told us`,
    items: ['No benefits yet', '66 or over', 'Needs help day and night'],
  },
  carryOn: {
    label: 'Carry on',
    items: [{ title: 'Attendance Allowance claim', detail: '3 of 8 answered' }],
  },
  ask: 'Ask me something',
  /** The conversations list in the menu, newest first. */
  threads: [
    { title: 'Attendance Allowance claim' },
    { title: 'Getting started' },
  ],
};
